import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Link as MuiLink,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Container from "../../components/ui/ui kit/Container";
import { CreateLeiService } from "../../api/services/create-lei";

const formatDate = (date?: string) => (date ? date.slice(0, 19).replace("T", " ") : "n/a");

const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1">{value === undefined || value === null || value === "" ? "n/a" : value}</Typography>
  </Grid>
);

export const OrderDetails = () => {
  const params = useParams();
  const order_tracking_id = params.lei;

  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const checkOrderStatus = useCallback(async () => {
    try {
      await CreateLeiService.orderStatus({ order_tracking_code: order_tracking_id });
    } catch (error) {
      console.log(error);
    }
  }, [order_tracking_id]);

  useEffect(() => {
    checkOrderStatus();
  }, [checkOrderStatus]);

  useEffect(() => {
    const requestLei = async () => {
      if (!order_tracking_id) return;
      setLoading(true);
      try {
        const res = await CreateLeiService.orderDetails({ order_tracking_code: order_tracking_id });
        setOrderData(res.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    requestLei();
  }, [order_tracking_id]);

  if (loading || !orderData) {
    return (
      <Container>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const ctx = orderData;
  const legalAddress = [ctx.legal_address_street, ctx.legal_address_house, ctx.legal_address_suite].filter(Boolean).join(" ");
  const legalCityLine = [ctx.legal_address_city, ctx.legal_address_postal_code].filter(Boolean).join(" ");
  const hqAddress = [ctx.headquarter_address_street, ctx.headquarter_address_house, ctx.headquarter_address_suite].filter(Boolean).join(" ");
  const hqCityLine = [ctx.headquarter_address_city, ctx.headquarter_address_postal_code].filter(Boolean).join(" ");

  return (
    <Container>
      <Helmet>
        <title>Order Details for LEI {ctx.lei_number || ctx.order_tracking_code} | LEIpapa</title>
        <meta
          name="description"
          content={`Order information for LEI ${ctx.lei_number || "n/a"}, legal name ${ctx.company_name}, entity status ${ctx.entity_status || "n/a"}${ctx.next_renewal_date ? `, next renewal date ${formatDate(ctx.next_renewal_date)}` : ""}`}
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
        <Box sx={{ py: { xs: 3, sm: 4 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" component="h1" fontWeight={700} sx={{ color: "primary.dark" }}>
              Order Details
            </Typography>
            <Button component={RouterLink} to="/orders" variant="outlined" startIcon={<ArrowBackIcon />}>
              Back to Orders
            </Button>
          </Stack>

          <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Legal Entity Information
            </Typography>
            <Grid container spacing={2}>
              <InfoRow label="Order Tracking Code" value={ctx.order_tracking_code} />
              <InfoRow label="Order Creation Date" value={formatDate(ctx.created_at)} />
              <InfoRow label="Legal Name" value={ctx.company_name} />
              <InfoRow label="Company Number" value={ctx.company_number} />
              <InfoRow
                label="LEI Code Issued"
                value={
                  ctx.lei_number ? (
                    <MuiLink component={RouterLink} to={`/lei-details/${ctx.lei_number}`} target="_blank" rel="noopener noreferrer">
                      {ctx.lei_number}
                    </MuiLink>
                  ) : undefined
                }
              />
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Order Status
                </Typography>
                <Chip
                  size="small"
                  label={ctx.status || "n/a"}
                  color={ctx.status === "Complete" ? "success" : "default"}
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <InfoRow label="Entity Status" value={ctx.entity_status} />
              <InfoRow label="Entity Type" value={ctx.entity_type} />
              <InfoRow label="Registration Authority" value={ctx.registration_authority} />
              <InfoRow label="Registration Authority ID" value={ctx.registration_authority_id} />
              <InfoRow label="Entity Legal Form" value={ctx.entity_legal_form} />
              <InfoRow label="Entity Creation Date" value={ctx.entity_creation_date ? formatDate(ctx.entity_creation_date) : undefined} />
              <InfoRow label="First Name" value={ctx.sa_first_name} />
              <InfoRow label="Last Name" value={ctx.sa_last_name} />
              <InfoRow label="Years to Support" value={ctx.multi_year_support} />
              <InfoRow label="Net Price" value={ctx.paid_amount != null ? `€${ctx.paid_amount}` : undefined} />
            </Grid>
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                  Legal Address
                </Typography>
                <Typography variant="body2">{legalAddress || "n/a"}</Typography>
                <Typography variant="body2">{legalCityLine}</Typography>
                <Typography variant="body2">{ctx.legal_address_country}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                  Headquarters Address
                </Typography>
                <Typography variant="body2">{hqAddress || "n/a"}</Typography>
                <Typography variant="body2">{hqCityLine}</Typography>
                <Typography variant="body2">{ctx.headquarter_address_country}</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
    </Container>
  );
};
